import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlignLeft, ChevronRight, X, LogOut } from 'lucide-react';
import { Logout } from '@/components/shared/Logout';
import { NavLink } from 'react-router-dom';
import { mobileNavItems } from '@/configs/nav/nav-items';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/hooks/useAuth';

export const MobileMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isOpen) {
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    } else {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        isOpen
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden"
        onClick={() => setIsOpen(true)}
        aria-label="Open mobile menu"
      >
        <AlignLeft className="h-6 w-6 md:h-8 md:w-8" />
      </Button>
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm"
              onClick={() => setIsOpen(false)}
            />
            <motion.div
              ref={menuRef}
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="fixed top-0 right-0 bottom-0 z-50 h-screen w-64 bg-background border-r shadow-lg flex flex-col"
            >
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-xl font-semibold">Navigation</h2>
                <Button
                  size="icon"
                  onClick={() => setIsOpen(false)}
                  className="rounded-full hover:bg-accent transition-colors duration-200"
                  aria-label="Close menu"
                >
                  <X size={24} />
                </Button>
              </div>
              <nav className="flex-1 overflow-y-auto">
                <ul className="space-y-2 p-4">
                  {mobileNavItems.map((item, i) => (
                    <motion.li
                      key={item.path}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.07 }}
                    >
                      <NavLink
                        to={item.path}
                        onClick={() => setIsOpen(false)}
                        className={({ isActive }) =>
                          `flex items-center justify-between px-4 py-3 rounded-lg transition-all duration-200 ${isActive
                            ? 'bg-primary/10 text-primary font-medium'
                            : 'text-foreground hover:bg-accent'
                          }`
                        }
                      >
                        <span>{item.title}</span>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </NavLink>
                    </motion.li>
                  ))}
                </ul>
                {isAuthenticated && (
                  <div className="p-4">
                    <Logout>
                      <Button
                        variant="destructive"
                        className="w-full flex items-center justify-center"
                      >
                        <LogOut size={18} className="mr-2" />
                        Log out
                      </Button>
                    </Logout>
                  </div>
                )}
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
